const SOURCE_TIME_ZONE = process.env.VUE_APP_SOURCE_TIME_ZONE || 'Asia/Shanghai'

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const NAIVE_DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T])(\d{2}):(\d{2})(?::(\d{2})(\.(\d{1,3}))?)?$/
const HAS_TIME_ZONE_RE = /(Z|[-+]\d{2}:\d{2})$/
const TIME_FIELD_KEY_RE = /(At|Time|Date|Deadline|Start|End)$/i

const timeZoneFormatterCache = new Map()

function getTimeZoneParts(date, timeZone) {
  const cacheKey = `${timeZone}:parts`
  let formatter = timeZoneFormatterCache.get(cacheKey)

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    })
    timeZoneFormatterCache.set(cacheKey, formatter)
  }

  const parts = formatter.formatToParts(date)
  const map = {}

  parts.forEach(part => {
    if (part.type !== 'literal') {
      map[part.type] = part.value
    }
  })

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second)
  }
}

function pad(value, length = 2) {
  return String(value).padStart(length, '0')
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function isSpecialObject(value) {
  return value instanceof Date ||
    (typeof Blob !== 'undefined' && value instanceof Blob) ||
    (typeof File !== 'undefined' && value instanceof File) ||
    (typeof FormData !== 'undefined' && value instanceof FormData) ||
    (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams)
}

function getTimeZoneOffsetMs(date, timeZone) {
  const parts = getTimeZoneParts(date, timeZone)
  const utcTime = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    0
  )

  return utcTime - date.getTime()
}

function zonedDateTimeToDate(parts, timeZone) {
  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millisecond
  )

  let offset = getTimeZoneOffsetMs(new Date(utcGuess), timeZone)
  let timestamp = utcGuess - offset
  const adjustedOffset = getTimeZoneOffsetMs(new Date(timestamp), timeZone)

  if (adjustedOffset !== offset) {
    offset = adjustedOffset
    timestamp = utcGuess - offset
  }

  return new Date(timestamp)
}

function parseNaiveDateTime(value) {
  const match = String(value).trim().match(NAIVE_DATE_TIME_RE)
  if (!match) return null

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] || 0),
    millisecond: Number((match[8] || '').padEnd(3, '0') || 0)
  }
}

function parseDateOnly(value) {
  const match = String(value).trim().match(DATE_ONLY_RE)
  if (!match) return null

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3])
  }
}

function buildLocalDate(parts) {
  const date = new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour || 0,
    parts.minute || 0,
    parts.second || 0,
    parts.millisecond || 0
  )

  return Number.isNaN(date.getTime()) ? null : date
}

function formatWithLocalParts(date, includeSeconds = true) {
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hour = pad(date.getHours())
  const minute = pad(date.getMinutes())
  const second = pad(date.getSeconds())

  return includeSeconds
    ? `${year}-${month}-${day}T${hour}:${minute}:${second}`
    : `${year}-${month}-${day}T${hour}:${minute}`
}

export function getSourceTimeZone() {
  return SOURCE_TIME_ZONE
}

export function getUserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

export function getUserTimeZoneLabel() {
  const zone = getUserTimeZone()
  const now = new Date()
  const offsetMinutes = -now.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const absoluteMinutes = Math.abs(offsetMinutes)
  const hours = pad(Math.floor(absoluteMinutes / 60))
  const minutes = pad(absoluteMinutes % 60)

  return `${zone} (UTC${sign}${hours}:${minutes})`
}

export function parseServerDateTime(value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getTime())
  if (typeof value === 'number') {
    const numericDate = new Date(value)
    return Number.isNaN(numericDate.getTime()) ? null : numericDate
  }

  const raw = String(value).trim()
  if (!raw) return null

  if (HAS_TIME_ZONE_RE.test(raw)) {
    const zonedDate = new Date(raw)
    return Number.isNaN(zonedDate.getTime()) ? null : zonedDate
  }

  const dateOnly = parseDateOnly(raw)
  if (dateOnly) {
    return buildLocalDate(dateOnly)
  }

  const naiveDateTime = parseNaiveDateTime(raw)
  if (naiveDateTime) {
    return zonedDateTimeToDate(naiveDateTime, SOURCE_TIME_ZONE)
  }

  const fallback = new Date(raw)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

export function toNormalizedResponseDateTime(value) {
  if (typeof value !== 'string') return value
  const raw = value.trim()
  if (!raw || HAS_TIME_ZONE_RE.test(raw) || !NAIVE_DATE_TIME_RE.test(raw)) {
    return value
  }

  const parsed = parseServerDateTime(raw)
  return parsed ? parsed.toISOString() : value
}

export function toServerDateTimeString(value) {
  if (!value) return value

  if (typeof value === 'string') {
    const raw = value.trim()
    if (!raw) return value
    if (HAS_TIME_ZONE_RE.test(raw)) {
      const zoned = new Date(raw)
      return Number.isNaN(zoned.getTime()) ? value : formatInTimeZone(zoned, SOURCE_TIME_ZONE, true)
    }
    if (!NAIVE_DATE_TIME_RE.test(raw)) {
      return value
    }
    const localDate = buildLocalDate(parseNaiveDateTime(raw))
    return localDate ? formatInTimeZone(localDate, SOURCE_TIME_ZONE, true) : value
  }

  const parsed = parseServerDateTime(value)
  return parsed ? formatInTimeZone(parsed, SOURCE_TIME_ZONE, true) : value
}

export function toLocalInputDateTime(value) {
  if (!value) return ''

  if (typeof value === 'string') {
    const raw = value.trim()
    if (!raw) return ''

    if (!HAS_TIME_ZONE_RE.test(raw) && NAIVE_DATE_TIME_RE.test(raw)) {
      const parts = parseNaiveDateTime(raw)
      if (!parts) return ''
      return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`
    }
  }

  const parsed = parseServerDateTime(value)
  return parsed ? formatWithLocalParts(parsed, false) : ''
}

export function formatInTimeZone(dateLike, timeZone = undefined, includeSeconds = true) {
  const date = dateLike instanceof Date ? dateLike : parseServerDateTime(dateLike)
  if (!date || Number.isNaN(date.getTime())) return ''

  const parts = timeZone ? getTimeZoneParts(date, timeZone) : {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds()
  }

  const year = pad(parts.year, 4)
  const month = pad(parts.month)
  const day = pad(parts.day)
  const hour = pad(parts.hour)
  const minute = pad(parts.minute)
  const second = pad(parts.second)

  return includeSeconds
    ? `${year}-${month}-${day}T${hour}:${minute}:${second}`
    : `${year}-${month}-${day}T${hour}:${minute}`
}

export function transformTimeFields(payload, transformValue) {
  if (Array.isArray(payload)) {
    return payload.map(item => transformTimeFields(item, transformValue))
  }

  if (!payload || typeof payload !== 'object' || isSpecialObject(payload)) {
    return payload
  }

  if (!isPlainObject(payload)) {
    return payload
  }

  const next = {}

  Object.keys(payload).forEach(key => {
    const value = payload[key]

    if (shouldTransformTimeField(key, value)) {
      next[key] = transformValue(value, key)
      return
    }

    next[key] = transformTimeFields(value, transformValue)
  })

  return next
}

export function shouldTransformTimeField(key, value) {
  return typeof value === 'string' && TIME_FIELD_KEY_RE.test(String(key))
}

export function normalizeResponseTimeFields(payload) {
  return transformTimeFields(payload, value => toNormalizedResponseDateTime(value))
}

export function normalizeRequestTimeFields(payload) {
  return transformTimeFields(payload, value => toServerDateTimeString(value))
}
